import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Typography, Paper, Alert } from '@mui/material';
import { Map as MapIcon, Trash2, Maximize2, Undo, Upload } from 'lucide-react';
import mapboxgl from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { kml as kmlToGeoJSON } from '@tmcw/togeojson';
import * as turf from '@turf/turf';
import wellknown from 'wellknown';
import MapboxCustomSearch from '../Map/MapboxCustomSearch';
import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';

/**
 * MapDrawField - Interactive map field for drawing polygons
 * Used within forms for adding/editing site boundaries
 */
const MapDrawField = ({ value, onChange, label, helperText, disabled, existingGeometry, onAddressFound, parentBoundary, singleGeometryOnly }) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const drawRef = useRef(null);
  const fileInputRef = useRef(null);

  const [area, setArea] = useState(0);
  const [isMapVisible, setIsMapVisible] = useState(false);
  const [hasStartedDrawing, setHasStartedDrawing] = useState(false);
  const [isMapReady, setIsMapReady] = useState(false);

  // { type: 'warning'|'error', text: string } | null
  const [uploadStatus, setUploadStatus] = useState(null);
  // Whether the currently drawn geometry extends outside the parent boundary
  const [containmentError, setContainmentError] = useState(false);
  // Geometry parsed from an uploaded file, waiting to be loaded onto the map
  const [pendingUploadGeometry, setPendingUploadGeometry] = useState(null);

  // Reverse geocode to get address from coordinates
  const reverseGeocode = async (lng, lat) => {
    try {
      const token = process.env.REACT_APP_MAPBOX_TOKEN;
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${token}&types=address,place,locality,neighborhood`
      );
      const data = await response.json();
      
      if (data.features && data.features.length > 0) {
        return data.features[0].place_name;
      }
      return null;
    } catch (error) {
      console.error('Reverse geocoding error:', error);
      return null;
    }
  };

  /**
   * Checks for a CRS declaration in a GeoJSON object.
   * Returns a warning string if a non-4326 CRS is found, null otherwise.
   * A missing crs field is treated as valid (modern GeoJSON always assumes WGS84).
   */
  const checkGeoJSONCRS = (geojson) => {
    if (!geojson.crs) return null;
    const name = geojson.crs?.properties?.name || '';
    const valid = [
      'EPSG:4326', 'urn:ogc:def:crs:EPSG::4326',
      'urn:ogc:def:crs:OGC:1.3:CRS84', 'WGS84', 'CRS84',
      'urn:ogc:def:crs:OGC::CRS84',
    ];
    if (valid.some(v => name === v || name.endsWith(v))) return null;
    return `File CRS is "${name}" — expected EPSG:4326 (WGS84). Geometry may be mispositioned on the map.`;
  };

  /**
   * Heuristic check: validates that all coordinates fall within WGS84 bounds.
   * Returns an error string if any coordinate is outside [-180,180] lng / [-90,90] lat.
   */
  const checkCoordinateRange = (geometry) => {
    try {
      const coords = turf.coordAll(turf.feature(geometry));
      for (const [lng, lat] of coords) {
        if (lng < -180 || lng > 180 || lat < -90 || lat > 90) {
          return `Coordinates out of WGS84 range detected (e.g., [${lng.toFixed(1)}, ${lat.toFixed(1)}]). The file may use a projected CRS (like UTM) instead of EPSG:4326.`;
        }
      }
    } catch (e) {
      // Fail open
    }
    return null;
  };

  /**
   * Strips the Z (altitude) coordinate from any GeoJSON geometry.
   * KML and some GeoJSON files include altitude which causes PostGIS type issues.
   */
  const stripZ = (coords) => {
    if (typeof coords[0] === 'number') return coords.slice(0, 2);
    return coords.map(stripZ);
  };
  const strip3D = (geometry) => ({ ...geometry, coordinates: stripZ(geometry.coordinates) });

  /**
   * Extracts a single GeoJSON geometry from any GeoJSON type.
   * FeatureCollections with multiple polygon features are merged into a MultiPolygon.
   */
  const extractGeometry = (geojson) => {
    let features = [];
    if (geojson.type === 'FeatureCollection') {
      features = geojson.features.filter(f => f.geometry);
    } else if (geojson.type === 'Feature') {
      features = [geojson];
    } else {
      return geojson; // bare geometry
    }
    if (features.length === 0) return null;
    if (features.length === 1) return features[0].geometry;
    // Merge multiple polygon features into a single MultiPolygon
    const coords = [];
    for (const f of features) {
      if (f.geometry.type === 'Polygon') coords.push(f.geometry.coordinates);
      else if (f.geometry.type === 'MultiPolygon') coords.push(...f.geometry.coordinates);
    }
    return coords.length === 0 ? null : { type: 'MultiPolygon', coordinates: coords };
  };

  /**
   * Returns true if `geometry` is entirely contained within `parentBoundary`.
   * Fails open (returns true) when either value cannot be parsed.
   */
  const isWithinParent = (geometry) => {
    if (!parentBoundary) return true;
    try {
      const parentGeom = typeof parentBoundary === 'string'
        ? wellknown.parse(parentBoundary)
        : parentBoundary;
      if (!parentGeom) return true;
      return turf.booleanWithin(turf.feature(geometry), turf.feature(parentGeom));
    } catch (e) {
      console.warn('Containment check failed:', e);
      return true;
    }
  };

  /**
   * Loads a geometry onto the draw canvas, flies to it, and fires onChange.
   */
  const loadGeometryOntoMap = async (geometry) => {
    if (!drawRef.current || !mapRef.current) return;
    drawRef.current.deleteAll();
    drawRef.current.add({ type: 'Feature', properties: {}, geometry });
    drawRef.current.changeMode('simple_select');
    setHasStartedDrawing(true);

    const fc = turf.featureCollection([turf.feature(geometry)]);
    const bbox = turf.bbox(fc);
    mapRef.current.fitBounds([[bbox[0], bbox[1]], [bbox[2], bbox[3]]], { padding: 60, maxZoom: 17, duration: 1000 });

    const calculatedArea = turf.area(fc);
    setArea(calculatedArea);

    const within = isWithinParent(geometry);
    setContainmentError(!within);
    if (!within) { onChange(null); return; }

    onChange({ geometry: wellknown.stringify(geometry), area: Math.round(calculatedArea) });

    if (onAddressFound) {
      const centroid = turf.center(fc);
      const [lng, lat] = centroid.geometry.coordinates;
      const address = await reverseGeocode(lng, lat);
      if (address) onAddressFound(address);
    }
  };

  // ─── File upload ─────────────────────────────────────────────────────────────

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ''; // allow re-uploading the same file
    setUploadStatus(null);
    setContainmentError(false);

    const fname = file.name.toLowerCase();
    const isKML = fname.endsWith('.kml');
    const isGeoJSON = fname.endsWith('.geojson') || fname.endsWith('.json');

    if (!isKML && !isGeoJSON) {
      setUploadStatus({ type: 'error', text: 'Unsupported file type. Please upload a .geojson, .json, or .kml file.' });
      return;
    }

    const text = await file.text();
    let geojson;
    let crsWarning = null;

    try {
      if (isKML) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/xml');
        geojson = kmlToGeoJSON(doc); // KML always uses WGS84 — no CRS warning
      } else {
        geojson = JSON.parse(text);
        crsWarning = checkGeoJSONCRS(geojson);
      }
    } catch (err) {
      setUploadStatus({ type: 'error', text: `Failed to parse file: ${err.message}` });
      return;
    }

    const rawGeometry = extractGeometry(geojson);
    const geometry = rawGeometry ? strip3D(rawGeometry) : null;
    if (!geometry) {
      setUploadStatus({ type: 'error', text: 'No polygon geometry found in the file.' });
      return;
    }
    if (!['Polygon', 'MultiPolygon'].includes(geometry.type)) {
      setUploadStatus({ type: 'error', text: `Expected a Polygon or MultiPolygon, got "${geometry.type}".` });
      return;
    }

    // For sites (singleGeometryOnly): reject files with multiple geometries
    if (singleGeometryOnly) {
      let featureCount = 0;
      if (geojson.type === 'FeatureCollection') {
        featureCount = (geojson.features || []).filter(f => f.geometry).length;
      } else {
        featureCount = 1;
      }
      if (featureCount > 1) {
        setUploadStatus({ type: 'error', text: `File contains ${featureCount} geometries. Site boundary must be a single polygon.` });
        return;
      }
      if (geometry.type === 'MultiPolygon') {
        setUploadStatus({ type: 'error', text: 'Site boundary must be a single Polygon, not a MultiPolygon.' });
        return;
      }
    }

    // Heuristic check: coordinates must fall within WGS84 bounds
    const rangeWarning = checkCoordinateRange(geometry);
    if (rangeWarning) {
      setUploadStatus({ type: 'error', text: rangeWarning });
      return;
    }

    // Show CRS warning (non-blocking — user may still proceed)
    if (crsWarning) {
      setUploadStatus({ type: 'warning', text: `⚠ CRS Warning: ${crsWarning}` });
    }

    // Containment check (blocking for plots)
    if (!isWithinParent(geometry)) {
      setUploadStatus({ type: 'error', text: 'The uploaded geometry extends outside the site boundary. The plot must be entirely within the site.' });
      return;
    }

    setPendingUploadGeometry(geometry);
    if (!isMapVisible) setIsMapVisible(true);
  };

  // Load pending upload geometry once the map is ready
  useEffect(() => {
    if (!isMapReady || !pendingUploadGeometry) return;
    loadGeometryOntoMap(pendingUploadGeometry);
    setPendingUploadGeometry(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMapReady, pendingUploadGeometry]);

  // Initialize map
  useEffect(() => {
    if (!isMapVisible || !mapContainerRef.current || mapRef.current) return;

    mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/satellite-streets-v11',
      center: [76.9539, 11.0161],
      zoom: 15,
    });

    drawRef.current = new MapboxDraw({
      displayControlsDefault: false,
      controls: { polygon: true, trash: true },
      defaultMode: 'draw_polygon',
    });

    mapRef.current.addControl(drawRef.current);
    
    // Add navigation controls (zoom) in the top right
    mapRef.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    // Handle drawing events
    const handleUpdate = async () => {
      let data = drawRef.current.getAll();
      if (data.features.length > 0) {
        setHasStartedDrawing(true);

        // For sites: enforce single polygon — keep only the latest drawn polygon
        if (singleGeometryOnly && data.features.length > 1) {
          const latest = data.features[data.features.length - 1];
          drawRef.current.deleteAll();
          drawRef.current.add(latest);
          drawRef.current.changeMode('simple_select');
          data = drawRef.current.getAll();
        }

        const allPolygons = data.features.map(f => f.geometry);
        const combinedGeometry = allPolygons.length === 1
          ? allPolygons[0]
          : {
              type: 'MultiPolygon',
              coordinates: allPolygons.map(g => g.coordinates),
            };
        
        const calculatedArea = turf.area(data);
        setArea(calculatedArea);

        // Containment check when a parent boundary is set (plot creation)
        const within = isWithinParent(combinedGeometry);
        setContainmentError(!within);
        if (!within) {
          onChange(null);
          return;
        }
        
        // Convert to WKT and pass to form
        const wkt = wellknown.stringify(combinedGeometry);
        onChange({
          geometry: wkt,
          area: Math.round(calculatedArea),
        });
        
        // Reverse geocode to get address from polygon centroid
        if (onAddressFound) {
          const centroid = turf.center(data);
          const [lng, lat] = centroid.geometry.coordinates;
          const address = await reverseGeocode(lng, lat);
          if (address) {
            onAddressFound(address);
          }
        }
      }
    };

    const handleDelete = () => {
      setArea(0);
      setHasStartedDrawing(false);
      setContainmentError(false);
      onChange(null);
    };
    
    // Hide instruction banner as soon as user starts interacting with the map
    const handleMapClick = () => {
      setHasStartedDrawing(true);
    };

    mapRef.current.on('draw.create', handleUpdate);
    mapRef.current.on('draw.update', handleUpdate);
    mapRef.current.on('draw.delete', handleDelete);
    mapRef.current.on('click', handleMapClick); // Hide banner on first click

    // Add keyboard shortcut for undo (Ctrl+Z)
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        
        // Get the current mode to determine what to undo
        const mode = drawRef.current.getMode();
        
        if (mode === 'draw_polygon') {
          // While drawing a polygon, pop the last vertex
          // MapboxDraw doesn't expose direct access to vertices during drawing,
          // but we can use the internal API
          const data = drawRef.current.getAll();
          if (data.features.length > 0) {
            const currentFeature = data.features[data.features.length - 1];
            if (currentFeature.geometry.coordinates[0].length > 1) {
              // Remove last coordinate point (vertex)
              currentFeature.geometry.coordinates[0].pop();
              drawRef.current.add(currentFeature);
            }
          }
        } else {
          // In other modes, delete the selected feature
          const selected = drawRef.current.getSelected();
          if (selected.features.length > 0) {
            drawRef.current.trash();
          }
        }
      }
    };

    // Add event listener to the map container for keyboard events
    const mapContainer = mapContainerRef.current;
    if (mapContainer) {
      mapContainer.setAttribute('tabindex', '0'); // Make it focusable
      mapContainer.addEventListener('keydown', handleKeyDown);
      mapContainer.focus(); // Focus the map so it receives keyboard events
    }

    // Load existing geometry if provided
    if (existingGeometry) {
      try {
        let geojson;
        if (typeof existingGeometry === 'string') {
          // Try parsing as WKT first
          geojson = wellknown.parse(existingGeometry);
        } else {
          geojson = existingGeometry;
        }

        if (geojson) {
          setHasStartedDrawing(true);
          const id = 'existing-polygon';
          drawRef.current.add({
            id,
            type: 'Feature',
            properties: {},
            geometry: geojson,
          });

          // Select the polygon for editing
          drawRef.current.changeMode('simple_select', { featureIds: [id] });

          // Fly to polygon
          const featureCollection = {
            type: 'FeatureCollection',
            features: [{
              type: 'Feature',
              geometry: geojson,
              properties: {},
            }],
          };

          mapRef.current.flyTo({
            center: turf.center(featureCollection).geometry.coordinates,
            zoom: 15,
            duration: 1000,
          });

          // Calculate area
          const calculatedArea = turf.area({ type: 'FeatureCollection', features: [{ type: 'Feature', geometry: geojson }] });
          setArea(calculatedArea);
        }
      } catch (error) {
        console.error('Error loading geometry:', error);
      }
    }

    // Resize map after DOM settles
    mapRef.current.on('load', () => {
      setIsMapReady(true); // Mark map as ready

      // Add parent boundary as reference overlay (for sub-site creation)
      if (parentBoundary) {
        try {
          let parentGeojson = typeof parentBoundary === 'string'
            ? wellknown.parse(parentBoundary)
            : parentBoundary;

          if (parentGeojson) {
            mapRef.current.addSource('parent-boundary', {
              type: 'geojson',
              data: {
                type: 'Feature',
                geometry: parentGeojson,
                properties: {}
              }
            });

            // Dashed outline of parent boundary
            mapRef.current.addLayer({
              id: 'parent-boundary-line',
              type: 'line',
              source: 'parent-boundary',
              paint: {
                'line-color': '#E0A458',
                'line-width': 3,
                'line-dasharray': [3, 3],
                'line-opacity': 0.8,
              },
            });

            // Semi-transparent fill
            mapRef.current.addLayer({
              id: 'parent-boundary-fill',
              type: 'fill',
              source: 'parent-boundary',
              paint: {
                'fill-color': '#E0A458',
                'fill-opacity': 0.05,
              },
            });

            // Fly to parent boundary if no existing geometry
            if (!existingGeometry) {
              const parentFC = turf.featureCollection([turf.feature(parentGeojson)]);
              const bbox = turf.bbox(parentFC);
              mapRef.current.fitBounds([[bbox[0], bbox[1]], [bbox[2], bbox[3]]], {
                padding: 50,
                maxZoom: 16,
                duration: 1000,
              });
            }
          }
        } catch (error) {
          console.error('Error loading parent boundary:', error);
        }
      }

      setTimeout(() => {
        if (mapRef.current) {
          mapRef.current.resize();
        }
      }, 100);
    });

    return () => {
      // Remove keyboard event listener
      const mapContainer = mapContainerRef.current;
      if (mapContainer) {
        mapContainer.removeEventListener('keydown', handleKeyDown);
      }
      
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [isMapVisible, existingGeometry, onChange, parentBoundary]);

  const handleClear = () => {
    if (drawRef.current) {
      drawRef.current.deleteAll();
      setArea(0);
      setHasStartedDrawing(false);
      setContainmentError(false);
      setUploadStatus(null);
      onChange(null);
    }
  };

  const handleUndo = () => {
    if (drawRef.current && mapRef.current) {
      // MapboxDraw has an undo feature through its API
      // Get the current mode and trigger undo
      drawRef.current.trash();
    }
  };

  const toggleMapVisibility = () => {
    setIsMapVisible(!isMapVisible);
  };

  return (
    <Box>
      {/* Header: label + action buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="body2" fontWeight={600} color="text.primary">
          {label || 'Site Boundary'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            startIcon={<Upload size={14} />}
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled}
            variant="outlined"
            sx={{ textTransform: 'none' }}
          >
            Upload File
          </Button>
          <Button
            size="small"
            startIcon={isMapVisible ? <Maximize2 size={14} /> : <MapIcon size={14} />}
            onClick={toggleMapVisibility}
            disabled={disabled}
            sx={{ textTransform: 'none' }}
          >
            {isMapVisible ? 'Hide Map' : 'Draw on Map'}
          </Button>
        </Box>
      </Box>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".geojson,.json,.kml"
        style={{ display: 'none' }}
        onChange={handleFileUpload}
      />

      {helperText && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          {helperText}
        </Typography>
      )}

      {/* Format hint */}
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        Supported: GeoJSON (.geojson / .json) · KML (.kml) · Files must use CRS EPSG:4326 (WGS84)
      </Typography>

      {/* Upload status alert */}
      {uploadStatus && (
        <Alert
          severity={uploadStatus.type === 'error' ? 'error' : 'warning'}
          onClose={() => setUploadStatus(null)}
          sx={{ mb: 1 }}
        >
          {uploadStatus.text}
        </Alert>
      )}

      {/* Containment error (shown when drawn polygon extends outside parent) */}
      {containmentError && (
        <Alert severity="error" sx={{ mb: 1 }}>
          Boundary extends outside the site. The plot must be entirely within the site boundary.
        </Alert>
      )}

      {isMapVisible && (
        <Paper
          elevation={2}
          sx={{
            height: 400,
            position: 'relative',
            borderRadius: 2,
            overflow: 'hidden',
            border: '2px solid',
            borderColor: containmentError ? 'error.main' : area > 0 ? 'success.light' : 'divider',
            transition: 'border-color 0.3s',
            // Style Mapbox controls to not overlap with our UI
            '& .mapboxgl-ctrl-top-left': {
              top: '80px', // Move down to avoid overlap with area display
              left: '10px',
            },
            '& .mapboxgl-ctrl-top-right': {
              top: '10px',
              right: '10px',
            },
            '& .mapbox-gl-draw_ctrl-draw-btn': {
              backgroundColor: 'white',
            },
          }}
        >
          <Box
            ref={mapContainerRef}
            sx={{
              width: '100%',
              height: '100%',
            }}
          />

          {/* Search Box - only render when map is ready */}
          {isMapReady && mapRef.current && <MapboxCustomSearch map={mapRef.current} inDialog={true} />}

          {/* Area display */}
          {area > 0 && (
            <Box
              sx={{
                position: 'absolute',
                top: 60, // Moved down to avoid overlap with search box
                left: 10,
                bgcolor: containmentError ? 'rgba(211,47,47,0.92)' : 'rgba(34, 196, 93, 0.95)',
                color: 'white',
                px: 2,
                py: 1,
                borderRadius: 2,
                boxShadow: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography variant="body2" fontWeight={600}>
                {containmentError ? '⚠ Outside boundary · ' : ''}Area: {(area / 4046.86).toFixed(2)} acres ({area.toFixed(0)} m²)
              </Typography>
              <Button
                size="small"
                startIcon={<Undo size={14} />}
                onClick={handleUndo}
                sx={{
                  color: 'white',
                  minWidth: 'auto',
                  p: 0.5,
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                  },
                }}
              >
                Undo
              </Button>
              <Button
                size="small"
                startIcon={<Trash2 size={14} />}
                onClick={handleClear}
                sx={{
                  color: 'white',
                  minWidth: 'auto',
                  p: 0.5,
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                  },
                }}
              >
                Clear
              </Button>
            </Box>
          )}
        </Paper>
      )}

      {!isMapVisible && area > 0 && (
        <Paper
          sx={{
            p: 2,
            bgcolor: 'success.50',
            border: '1px solid',
            borderColor: 'success.light',
            borderRadius: 2,
          }}
        >
          <Typography variant="body2" color="success.dark" fontWeight={600}>
            ✓ Polygon drawn: {(area / 4046.86).toFixed(2)} acres ({area.toFixed(0)} m²)
          </Typography>
        </Paper>
      )}

      {!isMapVisible && area === 0 && (
        <Paper
          sx={{
            p: 2,
            bgcolor: 'warning.50',
            border: '1px solid',
            borderColor: 'warning.light',
            borderRadius: 2,
          }}
        >
          <Typography variant="body2" color="warning.dark">
            No boundary set. Draw on the map or upload a GeoJSON / KML file.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default MapDrawField;
